import React, { forwardRef, useImperativeHandle, useState, useRef, useEffect, useContext, useMemo } from 'react';
import { Popup } from 'devextreme-react/popup';
import notify from 'devextreme/ui/notify';
import { DockviewReact, DockviewReadyEvent, DockviewApi, IDockviewPanelProps, themeAbyss } from 'dockview-react';

import 'dockview-react/dist/styles/dockview.css';

import NotificationContext from '../../NotificationContext';

import PortalApi from '../../api/PortalApi';

import DashboardComponentDto from '../../classes/dtos/portalmodule/DashboardComponentDto';
import DashboardGraphChangeDto from '../../classes/dtos/notificationmodule/DashboardGraphChangeDto';

import PathAIRowsLogic from '../../logics/pathaimodule/PathAIRowsLogic';
import PathParameterDto from '../../classes/dtos/pathcommonmodule/PathParameterDto';

import { ComponentBridge, FrontFunction, injectBridgeScript } from './ComponentBridge';

export interface PortalDashboardPopupHandle {
    Open: (dashboardId: number) => void;
}

interface GraphPanelParams {
    dashboardComponentId: number;
    version?: number;
}

const GRAPH_COMPONENT_TYPE = 0;

// Ponts des composants actuellement affiches, par id de composant : permet au front d'appeler
// une fonction d'un composant, ex. callComponentFunction(12, 'highlight', 'France').
const componentBridges = new Map<number, ComponentBridge>();

export const callComponentFunction = (dashboardComponentId: number, method: string, ...args: any[]): Promise<any> => {
    const bridge = componentBridges.get(dashboardComponentId);

    if (!bridge) {
        return Promise.reject(new Error(`Component ${dashboardComponentId} is not displayed`));
    }

    return bridge.Call(method, ...args);
};

export const emitComponentEvent = (dashboardComponentId: number, name: string, data?: any) => {
    componentBridges.get(dashboardComponentId)?.Emit(name, data);
};

// Fonctions du front appelables par les composants via pathta.call(name, ...args).
// Liste blanche : rien d'autre n'est accessible depuis l'iframe.
// Renseigne par PortalDashboardPopup tant qu'il est monte : ouvre la popup de message du front.
let messagePopupHandler: ((title: string, message: string) => void) | null = null;

// Options de pathta.call('getPathAIRows', pathId, options) ; parameters en camelCase cote composant.
interface PathAIRowsCallOptions {
    fieldReferenceIds?: number[] | null;
    filterParameters?: string | null;
    parameters?: Array<{ parameterName: string, fieldType: number, valueString?: string, valueInt32?: number, valueBoolean?: boolean, valueDate?: string, valueDateTime?: string, valueDateTimeOffset?: string }>;
    timeoutMs?: number;
}

const pathAIRowsLogic = new PathAIRowsLogic();

// signal : annule les chargements en cours quand le composant n'est plus affiche.
const buildFrontFunctions = (dashboardComponentId: number, signal: AbortSignal): Record<string, FrontFunction> => ({
    notify: (message: string, type: 'info' | 'success' | 'warning' | 'error' = 'info') => {
        notify(String(message), type, 3000);
    },
    showPopup: (title: string, message: string) => {
        messagePopupHandler?.(String(title), String(message));
    },
    getComponentId: () => dashboardComponentId,
    getPathAIRows: async (pathId: number, options: PathAIRowsCallOptions = {}) => {
        if (!Number.isInteger(pathId)) {
            throw new Error('getPathAIRows: pathId must be an integer');
        }

        const parameters = (options.parameters ?? []).map(e => new PathParameterDto(e.parameterName, e.fieldType, e.valueString ?? null, e.valueInt32 ?? null, e.valueBoolean ?? null, e.valueDate ?? null, e.valueDateTime ?? null, e.valueDateTimeOffset ?? null));

        const result = await pathAIRowsLogic.GetAllRowsAsync(pathId, options.fieldReferenceIds ?? null, parameters, options.filterParameters ?? null, { Signal: signal, TimeoutMs: options.timeoutMs });

        if (result.IsFailed()) {
            throw new Error(result.ErrorMessage);
        }

        return result.Result;
    }
});

const GraphPanel = (props: IDockviewPanelProps<GraphPanelParams>) => {
    const [html, setHtml] = useState<string | null>(null);
    const iframeRef = useRef<HTMLIFrameElement>(null);

    const dashboardComponentId = props.params.dashboardComponentId;

    useEffect(() => {
        setHtml(null);
        PortalApi.GetComponentChartAsync(dashboardComponentId).then((e) => {
            setHtml(e.IsFailed() ? '' : e.Result);
        });
    }, [dashboardComponentId, props.params.version]);

    // Le pont est recree a chaque nouveau HTML : l'iframe est rechargee, le composant renvoie 'ready'.
    useEffect(() => {
        if (html === null) {
            return;
        }

        const abortController = new AbortController();
        const bridge = new ComponentBridge(() => iframeRef.current?.contentWindow ?? null, buildFrontFunctions(dashboardComponentId, abortController.signal));
        const detach = bridge.Attach();
        componentBridges.set(dashboardComponentId, bridge);

        return () => {
            abortController.abort();
            detach();
            if (componentBridges.get(dashboardComponentId) === bridge) {
                componentBridges.delete(dashboardComponentId);
            }
        };
    }, [html, dashboardComponentId]);

    const bridgedHtml = useMemo(() => html === null ? null : injectBridgeScript(html), [html]);

    if (bridgedHtml === null) {
        return <div style={{ padding: 10 }}>Loading...</div>;
    }

    return (
        <iframe
            ref={iframeRef}
            title={`chart-${dashboardComponentId}`}
            srcDoc={bridgedHtml}
            style={{ width: '100%', height: '100%', border: 'none' }}
            sandbox="allow-scripts"
        />
    );
};

const components = {
    graphPanel: GraphPanel
};

const PortalDashboardPopup = forwardRef<PortalDashboardPopupHandle, {}>((_, ref) => {

    const context = useContext(NotificationContext);

    if (!context) {
        throw new Error("PortalDashboardPopup must be used within a NotificationContextProvider");
    }

    const dockviewApiRef = useRef<DockviewApi | null>(null);

    const [isOpen, setIsOpen] = useState(false);
    const [dashboardId, setDashboardId] = useState<number | null>(null);
    const [dashboardComponents, setDashboardComponents] = useState<DashboardComponentDto[] | null>(null);

    useImperativeHandle(ref, () => ({
        Open(dashboardId: number) {
            setDashboardId(dashboardId);
            setIsOpen(true);
            setDashboardComponents(null);

            PortalApi.GetDashboardAsync(dashboardId).then((e) => {
                setDashboardComponents(e.IsFailed() ? [] : e.Result.Components);
            });
        }
    }));

    const closeDialog = () => {
        setIsOpen(false);
    };

    const [messagePopup, setMessagePopup] = useState<{ title: string, message: string } | null>(null);

    useEffect(() => {
        messagePopupHandler = (title, message) => setMessagePopup({ title, message });

        return () => {
            messagePopupHandler = null;
        };
    }, []);

    // Rafraichit le dashboard ouvert selon le message recu, uniquement s'il est concerne.
    useEffect(() => {
        if (!isOpen || dashboardId === null) {
            return;
        }

        const panelId = (graphId: number) => `component-${graphId}`;

        const addPanel = (api: DockviewApi, dto: DashboardGraphChangeDto) => {
            api.addPanel({
                id: panelId(dto.GraphId),
                component: 'graphPanel',
                title: dto.Name,
                params: { dashboardComponentId: dto.GraphId, version: dto.Version }
            });
        };

        const handleGraphAdded = (payload: string) => {
            const dto: DashboardGraphChangeDto = JSON.parse(payload);
            const api = dockviewApiRef.current;

            if (dto.DashboardId !== dashboardId || api === null || api.getPanel(panelId(dto.GraphId))) {
                return;
            }

            addPanel(api, dto);
        };

        const handleGraphVersionAdded = (payload: string) => {
            const dto: DashboardGraphChangeDto = JSON.parse(payload);
            const api = dockviewApiRef.current;

            if (dto.DashboardId !== dashboardId || api === null) {
                return;
            }

            const panel = api.getPanel(panelId(dto.GraphId));

            if (panel) {
                // Changer le parametre version relance le chargement du graphique dans le panneau.
                panel.api.updateParameters({ version: dto.Version });
            } else {
                addPanel(api, dto);
            }
        };

        context.notificationConnection.on('DashboardGraphAdded', handleGraphAdded);
        context.notificationConnection.on('DashboardGraphVersionAdded', handleGraphVersionAdded);

        return () => {
            context.notificationConnection.off('DashboardGraphAdded', handleGraphAdded);
            context.notificationConnection.off('DashboardGraphVersionAdded', handleGraphVersionAdded);
        };
    }, [isOpen, dashboardId, context]);

    const populatePanels = (api: DockviewApi, dashboardComponents: DashboardComponentDto[]) => {
        api.clear();

        dashboardComponents
            .filter(component => component.Type === GRAPH_COMPONENT_TYPE)
            .forEach(component => {
                api.addPanel({
                    id: `component-${component.Id}`,
                    component: 'graphPanel',
                    title: component.Name,
                    params: { dashboardComponentId: component.Id }
                });
            });
    };

    const onReady = (event: DockviewReadyEvent) => {
        dockviewApiRef.current = event.api;

        if (dashboardComponents !== null) {
            populatePanels(event.api, dashboardComponents);
        }
    };

    useEffect(() => {
        if (dockviewApiRef.current !== null && dashboardComponents !== null) {
            populatePanels(dockviewApiRef.current, dashboardComponents);
        }
    }, [dashboardComponents]);

    return (
        <>
        <Popup
            visible={messagePopup !== null}
            onHiding={() => setMessagePopup(null)}
            hideOnOutsideClick={true}
            showCloseButton={true}
            showTitle={true}
            title={messagePopup?.title ?? ''}
            container=".dx-viewport"
            width={400}
            height="auto"
        >
            <div>{messagePopup?.message}</div>
        </Popup>
        <Popup
            visible={isOpen}
            onHiding={closeDialog}
            dragEnabled={false}
            hideOnOutsideClick={true}
            showCloseButton={true}
            showTitle={true}
            title={`Dashboard ${dashboardId ?? ''}`}
            container=".dx-viewport"
            width="calc(100vw - 40px)"
            height="calc(100vh - 40px)"
        >
            <div style={{ width: '100%', height: '100%' }}>
                <DockviewReact theme={themeAbyss} components={components} onReady={onReady} />
            </div>
        </Popup>
        </>
    );
});

export default PortalDashboardPopup;
